import type { Tier, TrackAssessmentResult } from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';

import type { AssessmentDataPoint } from './TimelineChartSvg';

type AssessmentHistoryEntry = Readonly<{
  grade: string;
  term: string;
  career_track: string | undefined;
  profile_strength: string | null | undefined;
}>;

const summarizeHistory = (dataPoints: readonly AssessmentDataPoint[]): AssessmentHistoryEntry[] =>
  dataPoints.map(({ gradeValue, term, result }) => ({
    grade: gradeValue,
    term,
    career_track: result.careerTrack?.name,
    profile_strength: result.profileStrength?.overallUnlockStatus,
  }));

export const useAgentContext = (
  assessmentData: TrackAssessmentResult | null,
  targetTrack: Tier | undefined,
  assessmentDataPoints: readonly AssessmentDataPoint[],
) => {
  useRegisterAgentContext({
    kind: 'visual',
    label: 'Career Track Timeline Chart',
    display: [
      "Timeline chart showing the user's career track progress from 9th grade to their current grade.",
      "The solid black line shows the user's maximum career track over time, determined by academics.",
      'For each assessment, a colored circle sits on the black line. The circle color indicates their non-academic profile strength: red means "Behind", yellow means "Developing", green means "Strong". The most recent assessment is shown as a larger circle with the user\'s profile image.',
      "The blue shaded area represents the user's target track range from their Career Blueprint — the track they are aiming for.",
      "A single set of gray dotted lines shows the range of possibilities. The lower gray line is roughly the user's minimum track. The gray lines also extend into the future with upward and downward slopes to show potential trajectory.",
      'When explaining the chart, focus on what the data actually shows for THIS user. Reference actual colors (e.g. "the yellow circle" not "the colored circle"). Mention their current maximum track, how it changed over time, what the circle colors mean for them specifically, and what the blue shaded target area represents. Always mention the color of their current assessment circle.',
    ].join(' '),
    data: {
      current_career_track: assessmentData?.careerTrack?.name ?? null,
      target_track_from_blueprint: targetTrack ?? null,
      profile_strength_status: assessmentData?.profileStrength?.overallUnlockStatus ?? null,
      assessment_history: summarizeHistory(assessmentDataPoints),
    },
  });
};
